/**
 * Collection grouping: compatible products before original products.
 *
 * snippets/collection-grouping.liquid groups the products of the current page on the server.
 * When the collection spans several pages only a global order is correct (every compatible
 * product of the whole result before any original), so this script fetches every page of the
 * current query through the Section Rendering API, builds that order and shows the slice that
 * belongs to the current page. Pagination links and counts stay as rendered by the server
 * because the number of items and pages does not change.
 *
 * States of the product list (data-collection-grouping-state, styled in assets/custom.css):
 *   pending - rendered by the server for multi-page results; the per-page order stays hidden
 *   merged  - the slice of the global order is shown
 *   error   - a page could not be loaded or did not match the current query; the products stay
 *             hidden and the status line offers a retry, so an incomplete order is never shown
 *
 * The theme's CollectionSection (assets/theme.js) replaces the contents of
 * .collection__dynamic-part on sort, filter, page-size, page and history changes. A
 * MutationObserver merges every new list; the requests of a superseded merge are aborted and
 * only fulfilled pages are cached. After a merge the section receives a
 * "collection-grouping:rendered" event so CollectionSection recalculates the colour swatches
 * of the inserted cards.
 */
(function () {
  'use strict';

  var LIST_SELECTOR = '.product-list[data-collection-grouping]';
  var STATE_ATTRIBUTE = 'data-collection-grouping-state';
  var STATUS_SELECTOR = '[data-collection-grouping-status]';
  var RETRY_SELECTOR = '[data-collection-grouping-retry]';
  var RENDERED_EVENT = 'collection-grouping:rendered';
  var MAX_CONCURRENT_REQUESTS = 4;

  function parseConfig(list) {
    var config;

    try {
      config = JSON.parse(list.getAttribute('data-collection-grouping'));
    } catch (error) {
      return null;
    }

    if (!config || typeof config !== 'object') {
      return null;
    }

    return {
      page: parseInt(config.page, 10) || 1,
      pages: parseInt(config.pages, 10) || 1,
      pageSize: parseInt(config.pageSize, 10) || 0,
      items: parseInt(config.items, 10) || 0,
      labels: config.labels && typeof config.labels === 'object' ? config.labels : {}
    };
  }

  function sameQuery(a, b) {
    return a.pages === b.pages && a.pageSize === b.pageSize && a.items === b.items;
  }

  function itemGroup(item) {
    return item.getAttribute('data-product-group') === 'original' ? 'original' : 'compatible';
  }

  function extractItems(list) {
    return Array.prototype.filter.call(list.children, function (child) {
      return child.classList.contains('product-item');
    }).map(function (item) {
      return { group: itemGroup(item), html: item.outerHTML };
    });
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function headingHtml(group, labels) {
    return '<h2 class="collection-grouping__heading" data-collection-group="' + group + '">' + escapeHtml(labels[group] || '') + '</h2>';
  }

  function renderStatus(list, labels, state) {
    var status = list.querySelector(STATUS_SELECTOR);

    if (!status) {
      status = document.createElement('p');
      status.className = 'collection-grouping__status';
      status.setAttribute('role', 'status');
      status.setAttribute('data-collection-grouping-status', '');
      list.insertBefore(status, list.firstChild);
    }

    if (state === 'error') {
      var retry = document.createElement('button');

      retry.type = 'button';
      retry.className = 'collection-grouping__retry link';
      retry.setAttribute('data-collection-grouping-retry', '');
      retry.textContent = labels.retry || '';

      status.textContent = (labels.error || '') + ' ';
      status.appendChild(retry);
    } else {
      status.textContent = labels.loading || '';
    }
  }

  function renderSlice(list, slice, labels) {
    // The layout (grid/list) is resolved now, from the cards currently in the list: the theme switches
    // their classes in place when the visitor changes the layout, also while other pages are loading.
    var referenceItem = list.querySelector('.product-item');
    var itemClassName = referenceItem ? referenceItem.className : '';
    var html = '';
    var currentGroup = null;

    slice.forEach(function (entry) {
      if (entry.group !== currentGroup) {
        html += headingHtml(entry.group, labels);
        currentGroup = entry.group;
      }

      html += entry.html;
    });

    list.innerHTML = html;

    if (itemClassName) {
      Array.prototype.forEach.call(list.querySelectorAll('.product-item'), function (item) {
        item.className = itemClassName;
      });
    }
  }

  // The query (path, sort and filter parameters) of the list that is being merged. It is read once per
  // merge: CollectionSection pushes the new URL before it replaces the list, so a later request of a
  // running merge must not read window.location again or it would fetch a page of another query.
  function currentQueryUrl() {
    var url = new URL(window.location.href);

    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.hash = '';

    return url.toString();
  }

  function queryKey(config, queryUrl) {
    return [config.pageSize, config.pages, config.items, queryUrl].join('|');
  }

  function CollectionGrouping(section) {
    this.section = section;
    this.sectionId = section.getAttribute('data-section-id');
    this.dynamicPart = section.querySelector('.collection__dynamic-part');
    this.pages = new Map(); // Fulfilled pages only: query key + page number -> extracted items
    this.handled = new WeakSet(); // Lists already merged (or waiting for a retry)
    this.run = 0;
    this.abortController = null;

    if (!this.dynamicPart || !this.sectionId) {
      return;
    }

    this.onClick = this.onClick.bind(this);
    this.dynamicPart.addEventListener('click', this.onClick);
    this.observer = new MutationObserver(this.process.bind(this));
    this.observer.observe(this.dynamicPart, { childList: true });
    this.process();
  }

  CollectionGrouping.prototype.destroy = function () {
    this.run += 1;

    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.dynamicPart) {
      this.dynamicPart.removeEventListener('click', this.onClick);
    }

    if (this.abortController) {
      this.abortController.abort();
    }
  };

  CollectionGrouping.prototype.onClick = function (event) {
    var retry = event.target.closest(RETRY_SELECTOR);
    var list = retry ? retry.closest(LIST_SELECTOR) : null;

    if (!list) {
      return;
    }

    event.preventDefault();
    this.pages.clear();
    this.merge(list);
  };

  CollectionGrouping.prototype.process = function () {
    var list = this.dynamicPart.querySelector(LIST_SELECTOR);

    if (!list || this.handled.has(list)) {
      return;
    }

    this.handled.add(list);
    this.merge(list);
  };

  CollectionGrouping.prototype.pageUrl = function (queryUrl, page) {
    var url = new URL(queryUrl);

    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', this.sectionId);

    return url.toString();
  };

  CollectionGrouping.prototype.fetchPage = function (queryUrl, page, config, signal) {
    return fetch(this.pageUrl(queryUrl, page), {
      credentials: 'same-origin',
      method: 'GET',
      signal: signal
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Page ' + page + ' request failed with status ' + response.status);
      }

      return response.text();
    }).then(function (content) {
      var list = new DOMParser().parseFromString(content, 'text/html').querySelector(LIST_SELECTOR);
      var pageConfig = list ? parseConfig(list) : null;

      if (!pageConfig) {
        throw new Error('Page ' + page + ' has no grouped product list');
      }

      if (pageConfig.page !== page || !sameQuery(pageConfig, config)) {
        throw new Error('Page ' + page + ' does not match the current query (page size, page count or product count changed)');
      }

      return extractItems(list);
    });
  };

  CollectionGrouping.prototype.fetchPages = function (pageNumbers, config, queryUrl, signal) {
    var self = this;
    var key = queryKey(config, queryUrl);
    var queue = pageNumbers.slice();
    var workers = [];
    var workerCount = Math.min(MAX_CONCURRENT_REQUESTS, queue.length);

    function next() {
      if (queue.length === 0 || signal.aborted) {
        return Promise.resolve();
      }

      var page = queue.shift();

      return self.fetchPage(queryUrl, page, config, signal).then(function (items) {
        // A superseded merge (aborted while its response was being read) leaves the cache alone.
        if (!signal.aborted) {
          self.pages.set(key + '|' + page, items);
        }

        return next();
      });
    }

    for (var i = 0; i < workerCount; i++) {
      workers.push(next());
    }

    return Promise.all(workers);
  };

  CollectionGrouping.prototype.merge = function (list) {
    var self = this;
    var run = ++this.run;
    var config = parseConfig(list);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (!config || config.pages <= 1 || config.pageSize <= 0) {
      // A single page is already in global order, nothing to merge.
      list.removeAttribute(STATE_ATTRIBUTE);
      return;
    }

    var controller = new AbortController();
    var queryUrl = currentQueryUrl(); // Frozen for this merge, see currentQueryUrl()
    var key = queryKey(config, queryUrl);
    var missingPages = [];

    this.abortController = controller;
    list.setAttribute(STATE_ATTRIBUTE, 'pending');
    renderStatus(list, config.labels, 'pending');

    // The current page is already rendered by the server, no need to fetch it again.
    this.pages.set(key + '|' + config.page, extractItems(list));

    for (var page = 1; page <= config.pages; page++) {
      if (!this.pages.has(key + '|' + page)) {
        missingPages.push(page);
      }
    }

    this.fetchPages(missingPages, config, queryUrl, controller.signal).then(function () {
      if (run !== self.run) {
        return;
      }

      var grouped = { compatible: [], original: [] };

      for (var page = 1; page <= config.pages; page++) {
        self.pages.get(key + '|' + page).forEach(function (entry) {
          grouped[entry.group].push(entry);
        });
      }

      var ordered = grouped.compatible.concat(grouped.original);

      if (ordered.length !== config.items) {
        throw new Error('Expected ' + config.items + ' products but the pages contain ' + ordered.length);
      }

      var start = (config.page - 1) * config.pageSize;

      renderSlice(list, ordered.slice(start, start + config.pageSize), config.labels);
      list.setAttribute(STATE_ATTRIBUTE, 'merged');
      self.section.dispatchEvent(new CustomEvent(RENDERED_EVENT, { bubbles: true, detail: { list: list } }));
    }).catch(function (error) {
      if (run !== self.run) {
        // Superseded by a newer merge, which aborted this one.
        return;
      }

      // Stop the remaining requests of this merge and keep the products hidden until a retry succeeds.
      controller.abort();
      self.abortController = null;
      list.setAttribute(STATE_ATTRIBUTE, 'error');
      renderStatus(list, config.labels, 'error');
      console.warn('[collection-grouping] The collection pages could not be merged.', error);
    });
  };

  var instances = new WeakMap();

  function mount(section) {
    if (instances.has(section)) {
      return;
    }

    instances.set(section, new CollectionGrouping(section));
  }

  function mountAll(root) {
    Array.prototype.forEach.call(root.querySelectorAll('[data-section-type="collection"]'), mount);
  }

  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    Array.prototype.forEach.call(event.target.querySelectorAll('[data-section-type="collection"]'), function (section) {
      var instance = instances.get(section);

      if (instance) {
        instance.destroy();
        instances.delete(section);
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mountAll(document);
    });
  } else {
    mountAll(document);
  }
})();
